import * as fabric from 'office-ui-fabric-react';
import { ChangeEvent, FC, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import * as api from '../../../../../../api/generated';
import { KendoLoading } from '../../../../../../components';
import PageHeader from '../../../../../../components/layout/PageHeader';
import { IWindowComponentProps } from '../../../../../../components/windows/WindowContext';
import * as models from '../../../../../../models';
import LocationConfigInfoScreenScheduleSettings from './LocationConfigInfoScreenScheduleSettings';
interface LocationConfigInfoScreenScheduleWindowProps {
    infoScreenId: number;
}

export interface IFilter {
    term: string;
}

interface IPractitionerScheduleElement extends models.IPractitionerSchedule {
    isSaved: boolean;
    isShown: boolean;
}

export interface IInfoScreenWorkScheduleSettings {
    title: string | null;
    isWorkScheduleTitleShown: boolean;
    options: fabric.IDropdownOption[];
}

enum ListShowing {
    showAll,
    showSelected,
    showNotSelected,
    showChanged
}

const LocationConfigInfoScreenScheduleWindow: FC<IWindowComponentProps<LocationConfigInfoScreenScheduleWindowProps>> = (props) => {
    const {
        componentProps: { infoScreenId },
        onClose
    } = props;

    const [listShowing, setListShowing] = useState<ListShowing>(ListShowing.showAll);
    const [allPractitioners, setAllPractitioners] = useState<IPractitionerScheduleElement[] | null>(null);

    const [filter, setFilter] = useState<IFilter>({ term: '' });

    const [areButtonsDisabled, setAreButtonsDisabled] = useState<boolean>(false);

    const [settings, setSettings] = useState<IInfoScreenWorkScheduleSettings | null>(null);

    const fetchData = async () => {
        try {
            const [workSchedulesResp, scheduleSettingsResp] = await Promise.all([
                api.workschedule.getWorkschedules(infoScreenId),
                api.workschedule.getInfoScreenWorkScheduleSettings(infoScreenId)
            ]);
            setAllPractitioners(workSchedulesResp);

            scheduleSettingsResp &&
                setSettings({
                    title: scheduleSettingsResp.title,
                    isWorkScheduleTitleShown: scheduleSettingsResp.isWorkScheduleTitleShown,
                    options: scheduleSettingsResp.options.map((x) => ({
                        key: x.key,
                        text: x.text ?? '',
                        selected: x.selected
                    }))
                });
        } catch (ex) {
            toast.error('Darbuotojų gauti nepavyko, sistemos klaida');
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const _ChoiceGroupOptions = [
        {
            key: 'A',
            text: 'Rodyti viską',
            onClick: () => setListShowing(ListShowing.showAll)
        },
        {
            key: 'B',
            text: 'Rodyti priskirtus',
            onClick: () => setListShowing(ListShowing.showSelected)
        },
        {
            key: 'C',
            text: 'Rodyti nepriskirtus',
            onClick: () => setListShowing(ListShowing.showNotSelected)
        }
    ];

    const handleElementShowUpdate = async (infoScreenPractitionerScheduleId: number, isShown: boolean) => {
        setAreButtonsDisabled(true);
        try {
            await api.workschedule.updateWorkScheduleElement({
                infoScreenPractitionerScheduleId: infoScreenPractitionerScheduleId,
                isShown: isShown
            });
            isShown ? toast.success('Įrašas bus atvaizduojamas ekrane') : toast.warning('Įrašas nebus atvaizduojamas ekrane');
        } catch (ex) {
            toast.error(ex.error[0]);
        } finally {
            fetchData();
            setAreButtonsDisabled(false);
        }
    };

    const _onFilter = (event?: ChangeEvent<HTMLInputElement> | undefined, newValue?: string | undefined) => {
        newValue && setFilter({ term: newValue });
    };

    const handleFilter = (list: IPractitionerScheduleElement[]): IPractitionerScheduleElement[] => {
        return list.filter(
            (i) =>
                (i.division !== null ? i.division.toLowerCase().indexOf(filter.term.toLowerCase()) > -1 : false) ||
                (i.firstName !== null ? i.firstName.toLowerCase().indexOf(filter.term.toLowerCase()) > -1 : false) ||
                (i.lastName !== null ? i.lastName.toLowerCase().indexOf(filter.term.toLowerCase()) > -1 : false) ||
                (i.qualification !== null ? i.qualification.toLowerCase().indexOf(filter.term.toLowerCase()) > -1 : false) ||
                (i.workplaceNumber !== null ? i.workplaceNumber.toLowerCase().indexOf(filter.term.toLowerCase()) > -1 : false)
        );
    };

    const handleItemsShown = (list: IPractitionerScheduleElement[]): IPractitionerScheduleElement[] => {
        switch (listShowing) {
            case ListShowing.showAll:
                return list;
            case ListShowing.showSelected:
                return list.filter((x) => x.isSaved === true);
            case ListShowing.showNotSelected:
                return list.filter((x) => x.isSaved === false);
            default:
                return list;
        }
    };

    const initColumns = [
        {
            key: 'column1',
            name: 'ID',
            fieldName: 'id',
            minWidth: 100,
            maxWidth: 100,
            isResizable: true
        },
        {
            key: 'column',
            name: 'Gydytojo kab. pav.',
            minWidth: 150,
            maxWidth: 150,
            isResizable: true,
            onRender: (item: IPractitionerScheduleElement) => item.workplaceName
        },
        {
            key: 'column2',
            name: 'Vardas Pavardė/Pavadinimas',
            minWidth: 200,
            maxWidth: 200,
            isResizable: true,
            onRender: (item: IPractitionerScheduleElement) =>
                (item.firstName && item.lastName) !== null
                    ? `${item.firstName} ${item.lastName}`
                    : item.workplaceName !== null
                    ? item.workplaceName
                    : ''
        },
        {
            key: 'column3',
            name: 'Pareigybė',
            minWidth: 150,
            maxWidth: 150,
            isResizable: true,
            onRender: (item: IPractitionerScheduleElement) => item.qualification
        },
        {
            key: 'column4',
            name: 'Padalinys',
            minWidth: 150,
            maxWidth: 150,
            isResizable: true,
            onRender: (item: IPractitionerScheduleElement) => item.division
        },
        {
            key: 'column5',
            name: 'Kabineto nr',
            minWidth: 100,
            maxWidth: 100,
            isResizable: true,
            onRender: (item: IPractitionerScheduleElement) => item.workplaceNumber
        },
        {
            key: 'column6',
            name: 'Darbo laikas',
            minWidth: 100,
            maxWidth: 100,
            isResizable: true,
            onRender: (item: IPractitionerScheduleElement) => {
                if (item.period != null && item.period.start.hours() > 0) {
                    return <b>{`${item.period.start.format('HH:mm')} - ${item.period.end.format('HH:mm')}`}</b>;
                } else {
                    return <b>Nenurodytas</b>;
                }
            }
        },
        {
            key: 'columm7',
            name: 'Veiksmai',
            maxWidth: 70,
            minWidth: 70,
            isResizable: false,
            onRender: (item: IPractitionerScheduleElement) => {
                return (
                    <div>
                        {item.isShown ? (
                            <fabric.IconButton
                                disabled={areButtonsDisabled}
                                onClick={() => handleElementShowUpdate(item.id, false)}
                                iconProps={{ iconName: 'redEye' }}
                            />
                        ) : (
                            <fabric.IconButton
                                disabled={areButtonsDisabled}
                                onClick={() => handleElementShowUpdate(item.id, true)}
                                iconProps={{ iconName: 'VisuallyImpaired' }}
                            />
                        )}
                    </div>
                );
            }
        }
    ];

    return (
        <>
            <PageHeader text="Tvarkaraštis" />
            <div className="ms-Grid" dir="ltr" style={{ margin: '20px 0px' }}>
                <LocationConfigInfoScreenScheduleSettings
                    setSettings={setSettings}
                    settings={settings}
                    infoScreenId={infoScreenId}
                    fetchSettings={fetchData}
                />
                {settings && settings.options.filter((x) => x.selected === true).length < 1 && (
                    <div>
                        <div className="ms-Grid-row">
                            {allPractitioners && (
                                <>
                                    <fabric.Label style={{ textAlign: 'left' }} htmlFor="searchBox">
                                        Paieška:
                                    </fabric.Label>
                                    <fabric.SearchBox
                                        id="searchBox"
                                        placeholder="Ieškoti pagal darbuotojo duomenis"
                                        onChange={_onFilter}
                                        value={filter.term}
                                        styles={{ root: { maxWidth: '300px', marginTop: '10px', marginBottom: '10px' } }}
                                    />
                                </>
                            )}
                            <div className="ms-Grid-col ms-sm2 ms-md2 ms-lg2">
                                {allPractitioners && (
                                    <>
                                        <div style={{ textAlign: 'left' }}>
                                            <fabric.Label htmlFor="choiceGroup">Darbuotojų rodymo tipas:</fabric.Label>
                                            <fabric.ChoiceGroup
                                                id="choiceGroup"
                                                defaultSelectedKey="A"
                                                options={_ChoiceGroupOptions}
                                                ariaLabelledBy="choiceGroup"
                                            />
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                        {allPractitioners ? (
                            handleFilter(handleItemsShown(allPractitioners)).length > 0 ? (
                                <div>
                                    <PageHeader text="Darbuotojų sąrašas" />

                                    <fabric.ShimmeredDetailsList
                                        checkboxVisibility={fabric.CheckboxVisibility.hidden}
                                        setKey={'1'}
                                        columns={initColumns}
                                        items={handleFilter(handleItemsShown(allPractitioners))}
                                        selectionMode={fabric.SelectionMode.none}
                                        enterModalSelectionOnTouch={true}
                                        selectionPreservedOnEmptyClick={true}
                                    />
                                </div>
                            ) : (
                                <fabric.Stack>
                                    <fabric.Stack.Item align="center">
                                        <fabric.Text variant="xLarge">Įrašų atitinkančių paiešką nėra</fabric.Text>
                                    </fabric.Stack.Item>
                                </fabric.Stack>
                            )
                        ) : (
                            <KendoLoading />
                        )}
                    </div>
                )}
            </div>
        </>
    );
};

export default LocationConfigInfoScreenScheduleWindow;
